import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, TableSortLabel, Paper, TextField, CircularProgress, Box,
  Chip, Tooltip, Button, Select, MenuItem, FormControl, InputLabel
} from '@mui/material';
import { format } from 'date-fns';
import useFetchEventTagResendStat from '../../hooks/useFetchEventTagResendStat.ts'; // adjust path accordingly

interface DetectorData {
  name: string;
  status: string;
  tags: number[];
}

interface DataRow {
  detectors: DetectorData[];
  tagId: string;
  fromDate: string;
  toDate: string;
  detections: number;
  resends: number;
  avgRssi: number;
  avgResendCounter: number;
  maxResendCounter: number;
  minResendCounter: number;
  resendsPerHour?: number;
  detectionsPerHour?: number;
  problemScore?: number;
}

const getScoreCategory = (score: number): { label: string; color: "error" | "warning" | "success" } => {
  if (score >= 80) return { label: "High Risk", color: "error" };
  if (score >= 50) return { label: "Moderate", color: "warning" };
  return { label: "Safe", color: "success" };
};

const MaterialUITable: React.FC = () => {
  const { data = [], isLoading, error } = useFetchEventTagResendStat();
  const [orderBy, setOrderBy] = useState<string>('fromDate');
  const [order, setOrder] = useState<'asc' | 'desc'>('asc');
  const [search, setSearch] = useState('');
  const [showAll, setShowAll] = useState(false);
  const [visibleRows, setVisibleRows] = useState(10);
  const [categoryFilter, setCategoryFilter] = useState('High Risk');

  const observer = useRef<IntersectionObserver | null>(null);
  const loadMoreRef = useRef<HTMLTableRowElement | null>(null);

  useEffect(() => {
    if (!showAll || !loadMoreRef.current) return;
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setVisibleRows(prev => Math.min(prev + 10, data.length));
      }
    });
    observer.current.observe(loadMoreRef.current);
  }, [showAll, visibleRows, data.length]);

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const filteredData = useMemo(() =>
    data.filter(row => {
      const category = getScoreCategory(row.problemScore ?? 0).label;
      const matchesCategory = categoryFilter === 'All' || category === categoryFilter;
      const matchesSearch = Object.values(row).some(value =>
        String(value).toLowerCase().includes(search)
      ) || row.detectors.some(d => d.name.toLowerCase().includes(search));
      return matchesCategory && matchesSearch;
    }), [data, search, categoryFilter]);

  const sortedData = useMemo(() =>
    [...filteredData].sort((a, b) => {
      const aVal = orderBy === 'name' ? a.detectors[0]?.name : a[orderBy];
      const bVal = orderBy === 'name' ? b.detectors[0]?.name : b[orderBy];
      return (aVal < bVal ? -1 : 1) * (order === 'asc' ? 1 : -1);
    }), [filteredData, order, orderBy]);

  const visibleData = showAll ? sortedData.slice(0, visibleRows) : sortedData.slice(0, 10);

  return (
    <Paper style={{ maxHeight: '800px', overflowY: 'auto' }}>
      <Box display="flex" gap={1} p={2} alignItems="center">
        <Box flex={5}>
          <TextField
            variant="outlined"
            fullWidth
            size="small"
            placeholder="Search..."
            value={search}
            onChange={e => setSearch(e.target.value.toLowerCase())}
          />
        </Box>
        <Box flex={3}>
          <FormControl fullWidth size="small">
            <InputLabel>Category</InputLabel>
            <Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
              <MenuItem value="All">All</MenuItem>
              <MenuItem value="High Risk">High Risk</MenuItem>
              <MenuItem value="Moderate">Moderate</MenuItem>
              <MenuItem value="Safe">Safe</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box flex={2}>
          <Button fullWidth variant="outlined" onClick={() => setShowAll(!showAll)}>
            {showAll ? 'Show Top 10' : 'Show All'}
          </Button>
        </Box>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
      ) : error ? (
        <Box color="error.main" p={4}>Error: {error.message}</Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Tag ID</TableCell>
                <TableCell>From Date</TableCell>
                <TableCell>To Date</TableCell>
                <TableCell align="right">Detections</TableCell>
                <TableCell align="right">Resends</TableCell>
                <TableCell align="right">Avg RSSI</TableCell>
                <TableCell align="right">Avg Resend</TableCell>
                <TableCell align="right">Max Resend</TableCell>
                <TableCell align="right">Min Resend</TableCell>
                <TableCell align="right">Score</TableCell>
                <TableCell>Category</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {visibleData.map((row, idx) => {
                const category = getScoreCategory(row.problemScore ?? 0);
                return (
                  <TableRow key={idx}>
                    <TableCell>{row.detectors[0]?.name || 'N/A'}</TableCell>
                    <TableCell>{row.tagId}</TableCell>
                    <TableCell>{format(new Date(row.fromDate), 'PPpp')}</TableCell>
                    <TableCell>{format(new Date(row.toDate), 'PPpp')}</TableCell>
                    <TableCell align="right">{row.detections}</TableCell>
                    <TableCell align="right">{row.resends}</TableCell>
                    <TableCell align="right">{row.avgRssi}</TableCell>
                    <TableCell align="right">{row.avgResendCounter}</TableCell>
                    <TableCell align="right">{row.maxResendCounter}</TableCell>
                    <TableCell align="right">{row.minResendCounter}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Score = 0.6*Resends/hr + 0.5*AvgResend + 0.3*RSSI - 0.4*Detections/hr">
                        <span>{row.problemScore?.toFixed(2)}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Chip label={category.label} color={category.color} size="small" />
                    </TableCell>
                  </TableRow>
                );
              })}
              {showAll && visibleRows < sortedData.length && (
                <TableRow ref={loadMoreRef}>
                  <TableCell colSpan={12} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default MaterialUITable;
